import React from 'react';
import { find, findIndex, cloneDeep, remove, orderBy } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import PopOverMenu from '../../PopOverMenu';
import ColumnHeader, { SortType } from '../../ColumnHeader';
import { ComparatorTypes, comparator } from '../../../utils/comparator';
import { PlayerCard, MemberCard } from '../../cards';
import { getRatingDetails, setRatingChoice } from '../../../utils';
import { isEventOfficialHsEvent } from '../../../utils/events';
import { setPlayersTabState } from '../../../actions/event-actions';
import { acceptanceRankingValues, acceptanceBucketTypes } from '../../../utils/eventConstants';
import { pluralize } from '../../../utils/formatting';

import styles from '../../../pages/EventProfiles/event.scss';

import Bucket from './Bucket';

const _ = { find, findIndex, cloneDeep, remove, orderBy };

const PlayersTabContent = (props) => {
    const {
        isCurrentMemberAdmin,
        showRatingNotLoggedIn,
        sendMessage,
        hideUtrLabel,
        'data-testid': dataTestId,
    } = props;

    const { auth, events } = useSelector((state) => state);
    const dispatch = useDispatch();

    const event = events.currentEvent.data || {};
    const { playersTabState, acceptance, data } = events.currentEvent;
    const isPtsEvent = _.find(data.eventTours, { name: 'UTR Pro Tennis Tour' });
    const isRegistrationClosed = event?.eventState?.registrationState?.value === 'registration_closed';
    const { sportTypeId } = event;

    const {
        sortParam,
        sortType,
        sortListParam,
        sortListType,
    } = playersTabState || {};

    const acceptanceData = _.cloneDeep(acceptance?.[playersTabState.divisionId]);

    const onSelectPlayerDivision = (selected) => {
        if (selected.value === 'errors') {
            playersTabState.divisionId = selected.value;
            playersTabState.divisionName = selected.label;
            playersTabState.registeredCount = (event.membersWithoutProfile && event.membersWithoutProfile.length) || 0;
            playersTabState.selectedStatus = {
                value: 'registered',
                label: 'Registered',
            };
        } else {
            playersTabState.divisionId = selected.value;
            playersTabState.divisionName = selected.label;
            playersTabState.registeredCount = event.eventDivisions.find(div => div.id === selected.value).registeredCount;
            playersTabState.selectedStatus = {
                value: 'registered',
                label: 'Registered',
            };
        }
        dispatch(setPlayersTabState(playersTabState));
    };

    const onSelectPlayerStatus = (selected) => {
        playersTabState.selectedStatus = selected;
        dispatch(setPlayersTabState(playersTabState));
    };

    const getTeamObject = (player, partner) => {
        const team = {
            players: [],
        };
        if (player && partner) {
            team.players.push(player);
            team.players.push(partner);
        } else if (player) {
            team.players.push(player);
        }
        return team;
    };

    const renderDivisonMenuItem = () => {
        const menuItems = event.eventDivisions.map(div => ({
            value: div.id,
            label: div.name,
            divisionFormatType: div.divisionFormatType,
        }));

        if (event.membersWithoutProfile && event.membersWithoutProfile.length) {
            menuItems.push({ value: 'errors', label: 'Registration Errors' });
        }
        return (
            <div className="d-flex">
                {menuItems.length === 1 && (
                    <h6 className="text-uppercase" data-testid={dataTestId ? `${dataTestId}.division-name` : undefined}>{event.eventDivisions[0].name}</h6>
                )}
                {menuItems.length !== 1 && (
                    <PopOverMenu
                        menuItems={menuItems}
                        onSelected={(selected) => onSelectPlayerDivision(selected)}
                        alignLeft={true}
                    >
                        <h6 className={`${styles.anchorClass} text-uppercase pl8`} data-testid={dataTestId ? `${dataTestId}.division-selector` : undefined}>
                            {playersTabState && playersTabState.divisionName}
                            <i className={`${styles.arrow} uts uts-expand`} />
                        </h6>
                    </PopOverMenu>
                )}
                {isCurrentMemberAdmin && (
                    <h6 className="border-left ml16 pl16">
                        <PopOverMenu
                            anchorClass={`${styles.anchorClass} pl8`}
                            popOverMenuClass="flex-right"
                            menuItems={[
                                { value: 'registered', label: 'Registered' },
                                { value: 'withdrawn', label: 'Withdrawn' },
                            ]}
                            onSelected={onSelectPlayerStatus}
                            alignLeft={true}
                        >
                            <span className="text-uppercase">
                                {playersTabState && playersTabState.selectedStatus && playersTabState.selectedStatus.label}
                            </span>
                            <i className={`${styles.arrow} uts uts-expand`} />
                        </PopOverMenu>
                    </h6>
                )}
            </div>
        );
    };

    const teamList = [];
    const playerIds = [];
    const withdrawnTeamList = [];
    const withdrawnPlayerIds = [];
    let playerCount = 0;
    let isWTARankType = false;

    if (event && playersTabState && playersTabState.selectedStatus) {
        switch (playersTabState.selectedStatus.value) {
            case 'registered':
                if (event.registeredPlayers.length > 0) {
                    event.registeredPlayers.forEach((player) => {
                        const playerDivision = _.find(player.registeredDivisions, { divisionId: playersTabState.divisionId });
                        if (playerDivision) {
                            if (playerIds.includes(player.id)) {
                                // Do nothing
                            } else {
                                playerIds.push(player.id);
                                let playerPartner;
                                // get partners list in this division
                                const partnerIds = playerDivision.partners;
                                partnerIds.every((id) => {
                                    const partner = _.find(event.registeredPlayers, { id });
                                    if (partner) {
                                        playerIds.push(partner.id);
                                        playerPartner = partner;
                                        return false;
                                    }
                                    return null;
                                });
                                const team = playerDivision.isPayee ? getTeamObject(player, playerPartner) : getTeamObject(playerPartner, player);
                                if (team && team.players.length > 0) {
                                    playerCount += team.players.length;
                                    teamList.push(team);
                                }
                            }
                        }
                    });
                }
                break;
            case 'withdrawn':
                if (event.withdrawnPlayers.length > 0) {
                    event.withdrawnPlayers.forEach((player) => {
                        const playerDivision = _.find(player.registeredDivisions, { divisionId: playersTabState.divisionId });
                        if (playerDivision) {
                            if (playerIds.includes(player.id)) {
                                // Do nothing
                            } else {
                                playerIds.push(player.id);
                                const team = getTeamObject(player);
                                if (team && team.players.length > 0) {
                                    playerCount += team.players.length;
                                    teamList.push(team);
                                }
                            }
                        }
                    });
                }
                break;
            default:
                break;
        }
    }

    const isCurrentMemberPlaying = event && (event.currentMember.isPlaying);
    let showPlayerStatus = isCurrentMemberAdmin;
    const eventAllowOffPlatformPayments = event && event.allowOffPlatformPayments;
    let showPlayerPaymentStatus = isCurrentMemberAdmin && !eventAllowOffPlatformPayments;
    let currentDivision = null;
    currentDivision = event && playersTabState && _.find(event.eventDivisions, { id: playersTabState.divisionId });
    const isFreeEvent = currentDivision && currentDivision.price === 'Free';
    if (event && event.utrType && event.utrType.id === 2 && playersTabState) {
        if (currentDivision && currentDivision.price === 'Free') {
            showPlayerPaymentStatus = false;
        }
    }
    let showPlayers = true;
    if (!isCurrentMemberAdmin && (event && !event.playerVisibility)) {
        showPlayers = false;
    }

    let playerActions = null;
    let withdrawnPlayerActions = null;
    if (isCurrentMemberAdmin && playersTabState && playersTabState.selectedStatus) {
        switch (playersTabState.selectedStatus.value) {
            case 'registered':
                if (event && event.paymentProviderType && event.paymentProviderType.id && event.paymentProviderType.id === 2) {
                    playerActions = [
                        { label: 'Manage', value: 'withdraw' },
                    ];
                } else {
                    playerActions = event.allowOffPlatformPayments ? [
                        { label: 'Withdraw', value: 'withdraw' },
                    ] : [
                        { label: 'Withdraw', value: 'withdraw' },
                        { label: 'Refund entry fees', value: 'refund' },
                    ];
                }
                break;
            case 'withdrawn':
                if (event && event.paymentProviderType && event.paymentProviderType.id && event.paymentProviderType.id === 2) {
                    playerActions = [
                        { label: 'Manage', value: 'refund' },
                    ];
                } else {
                    playerActions = event.allowOffPlatformPayments ? [] : [
                        { label: 'Refund entry fees', value: 'refund' },
                    ];
                }
                break;
            default:
                break;
        }
    }

    if (isFreeEvent) {
        const refundActionIndex = _.findIndex(playerActions, { value: 'refund' });
        if (refundActionIndex !== -1) {
            playerActions[refundActionIndex].show = false;
        }
    }

    // Start sort logic
    const getSortValue = (player, sortPrm) => {
        const utrType = event.utrType.value;
        const userIsPowered = (auth.user && auth.user.isPowered) || false;
        const showDecimals = isCurrentMemberAdmin;
        switch (sortPrm) {
            case 'singlesUtr': {
                const playerSinglesRating = getRatingDetails(setRatingChoice({ player, ratingType: 'singles', utrType }), userIsPowered, showDecimals);
                return playerSinglesRating.sortRating;
            }
            case 'doublesUtr': {
                const playerDoublesRating = getRatingDetails(setRatingChoice({ player, ratingType: 'doubles', utrType }), userIsPowered, showDecimals);
                return playerDoublesRating.sortRating;
            }
            default:
                return player[sortPrm];
        }
    };

    const getComparatorType = (srtParam) => {
        switch (srtParam) {
            case 'singlesUtr':
            case 'doublesUtr':
            case 'wtaOrder':
            case 'utrOrder':
                return ComparatorTypes.NUMBER;
            default:
                return ComparatorTypes.STRING;
        }
    };

    teamList.forEach((team) => {
        const player1 = team.players[0];
        let player2 = {};
        let doubles = false;
        if (team.players.length > 1) {
            doubles = true;
            // eslint-disable-next-line prefer-destructuring
            player2 = team.players[1];
        }
        switch (sortParam) {
            case 'name':
                team.primarySortValue = doubles ? `${player1.lastName} ${player2.lastName}` : player1.lastName;
                team.secondarySortValue = doubles ? `${player1.firstName} ${player2.firstName}` : player1.firstName;
                break;
            case 'singlesUtr':
            case 'doublesUtr': {
                const player1Rating = getSortValue(player1, sortParam);
                const player2Rating = getSortValue(player2, sortParam);
                team.primarySortValue = doubles ? (player1Rating + player2Rating) / 2 : player1Rating;
                break;
            }
            default:
                team.primarySortValue = '';
                break;
        }
    });

    const getComparatorArguments = (srtParam, srtType) => {
        const args = [];
        const primarySortArg = {
            type: getComparatorType(srtParam),
            field: 'primarySortValue',
            descend: srtType === SortType.DESC,
        };
        args.push(primarySortArg);
        if (['name', 'wtaOrder', 'utrOrder'].includes(srtParam)) {
            const seconfarySortArg = {
                type: getComparatorType(srtParam),
                field: 'secondarySortValue',
                descend: srtType === SortType.DESC,
            };
            args.push(seconfarySortArg);
        }
        return args;
    };

    const currentMember = isCurrentMemberPlaying ? _.find(event.registeredPlayers, { memberId: auth.user.id }) : [];

    teamList.sort(comparator(getComparatorArguments(sortParam, sortType)));
    // End sort logic

    const isOfficialHSEvent = isEventOfficialHsEvent(event);

    let buckets = [];
    let bucketPlayers = 0;
    let registeredPlayer;
    let withdrawnPlayer;
    let populatedPlayers;

    const playerPtsRanks = {};
    if (acceptanceData?.loaded) {
        isWTARankType = acceptanceData.rankingType === acceptanceRankingValues.WTA;
        showPlayerStatus = isCurrentMemberAdmin && !isWTARankType;
        showPlayerPaymentStatus = showPlayerPaymentStatus && !isWTARankType;
        buckets = _.orderBy(acceptanceData.buckets, ['order'], ['asc']);
        buckets.forEach((bucket, b) => {
            populatedPlayers = [];
            bucket.players.forEach(player => {
                bucketPlayers += 1;
                registeredPlayer = _.find(event.registeredPlayers, { memberId: player.memberId });
                if (registeredPlayer) {
                    populatedPlayers.push({
                        ...registeredPlayer,
                        rank: player.rank,
                        wtaOrder: player.position,
                        utrOrder: player.secondaryPosition,
                        acceptanceType: player.acceptanceType,
                        acceptanceListWildcardStateId: player.acceptanceListWildcardStateId,
                    });
                    playerPtsRanks[registeredPlayer.id] = player.rank;
                } else {
                    withdrawnPlayer = _.find(event.withdrawnPlayers, { memberId: player.memberId });
                    if (withdrawnPlayer) {
                        if (isRegistrationClosed) {
                            withdrawnPlayerIds.push(withdrawnPlayer.id);
                            withdrawnTeamList.push({
                                ...withdrawnPlayer,
                                rank: player.rank,
                                wtaOrder: player.position,
                                utrOrder: player.secondaryPosition,
                                acceptanceType: player.acceptanceType,
                                withdrawTag: bucket.type && `${bucket.type.charAt(0)}WD`,
                                acceptanceListWildcardStateId: player.acceptanceListWildcardStateId,
                            });
                        } else {
                            populatedPlayers.push({
                                ...withdrawnPlayer,
                                isWithdrawn: true,
                                rank: player.rank,
                                wtaOrder: player.position,
                                utrOrder: player.secondaryPosition,
                                acceptanceType: player.acceptanceType,
                                acceptanceListWildcardStateId: player.acceptanceListWildcardStateId,
                            });
                        }
                        playerPtsRanks[withdrawnPlayer.id] = player.rank;
                    }
                }
            });

            if (bucket.type === acceptanceBucketTypes.ALTERNATE && isWTARankType && playersTabState.sortAcceptanceType) {
                populatedPlayers.forEach(player => {
                    switch (playersTabState.sortAcceptanceType) {
                        case acceptanceRankingValues.WTA:
                            player.primarySortValue = player.wtaOrder;
                            player.secondarySortValue = getSortValue(player, 'singlesUtr');
                            break;
                        case acceptanceRankingValues.UTR:
                            player.primarySortValue = player.utrOrder;
                            player.secondarySortValue = getSortValue(player, 'singlesUtr');
                            break;
                        default:
                            player.primarySortValue = '';
                            break;
                    }
                });
                populatedPlayers.sort(comparator(getComparatorArguments(sortListParam, sortListType)));
            }
            buckets[b].players = populatedPlayers;
        });
    }

    const getAlternateListOrder = (acceptanceType) => {
        playersTabState.sortAcceptanceType = acceptanceType;
        playersTabState.sortListParam = acceptanceType === acceptanceRankingValues.WTA ? 'wtaOrder' : 'utrOrder';
        playersTabState.sortListType = SortType.ASC;
        dispatch(setPlayersTabState(playersTabState));
    };

    if (isCurrentMemberAdmin && isRegistrationClosed && acceptanceData?.loaded) {
        const isStripePayment = event && event.paymentProviderType && event.paymentProviderType.id && event.paymentProviderType.id === 2;
        const isWtaAcceptanceType = acceptanceData.rankingType === acceptanceRankingValues.WTA;
        const mainList = buckets.find(b => b.type === acceptanceBucketTypes.MAIN);
        const hasQualifyingList = buckets.find(b => b.type === acceptanceBucketTypes.QUALIFYING);
        const hasSpecialExemptCount = (mainList.countSpecialExempt ?? 0) > 0;
        const canShowRefund = !isStripePayment && !event.allowOffPlatformPayments && !isFreeEvent;

        buckets.forEach((bucket, i) => {
            let movePlayerActions = [];
            switch (bucket.type) {
                case acceptanceBucketTypes.MAIN:
                    buckets[i].playerActions = [
                        { label: `${isStripePayment ? 'Manage' : 'Withdraw'}`, value: 'withdraw' },
                        ...(canShowRefund ? [{ label: 'Refund entry fees', value: 'refund' }] : []),
                        { label: 'Tag player', value: 'tag' },
                    ];
                    break;
                case acceptanceBucketTypes.QUALIFYING:
                    movePlayerActions = [{ label: 'Move Player to Main', value: acceptanceBucketTypes.MAIN }];
                    if (isWtaAcceptanceType) {
                        movePlayerActions = [
                            { label: 'Move Player to Main WTA', value: acceptanceBucketTypes.MAIN, acceptanceType: acceptanceRankingValues.WTA },
                            { label: 'Move Player to Main UTR', value: acceptanceBucketTypes.MAIN, acceptanceType: acceptanceRankingValues.UTR },
                            ...(hasSpecialExemptCount ? [{ label: 'Move Player to Special Exempt', value: 'specialExempt' }] : []),
                        ];
                    }
                    buckets[i].playerActions = [
                        ...movePlayerActions,
                        { label: `${isStripePayment ? 'Manage' : 'Withdraw'}`, value: 'withdraw' },
                        ...(canShowRefund ? [{ label: 'Refund entry fees', value: 'refund' }] : []),
                        { label: 'Tag player', value: 'tag' },
                    ];
                    break;
                case acceptanceBucketTypes.ALTERNATE:
                    movePlayerActions = [
                        { label: 'Move Player to Main', value: acceptanceBucketTypes.MAIN },
                        ...(hasQualifyingList ? [{ label: 'Move Player to Qualifying', value: acceptanceBucketTypes.QUALIFYING }] : []),
                    ];
                    if (isWtaAcceptanceType) {
                        movePlayerActions = [
                            { label: 'Move Player to Main WTA', value: acceptanceBucketTypes.MAIN, acceptanceType: acceptanceRankingValues.WTA },
                            { label: 'Move Player to Main UTR', value: acceptanceBucketTypes.MAIN, acceptanceType: acceptanceRankingValues.UTR },
                            ...(hasQualifyingList ? [
                                { label: 'Move Player to Qualifying WTA', value: acceptanceBucketTypes.QUALIFYING, acceptanceType: acceptanceRankingValues.WTA },
                                { label: 'Move Player to Qualifying UTR', value: acceptanceBucketTypes.QUALIFYING, acceptanceType: acceptanceRankingValues.UTR },
                            ] : []),
                            ...(hasSpecialExemptCount ? [{ label: 'Move Player to Special Exempt', value: 'specialExempt' }] : []),
                        ];
                    }
                    buckets[i].playerActions = [
                        ...movePlayerActions,
                        { label: `${isStripePayment ? 'Manage' : 'Withdraw'}`, value: 'withdraw' },
                        ...(canShowRefund ? [{ label: 'Refund entry fees', value: 'refund' }] : []),
                        { label: 'Tag player', value: 'tag' },
                    ];
                    break;
                default:
                    break;
            }
        });
        if (isStripePayment) {
            withdrawnPlayerActions = [
                { label: 'Manage', value: 'refund' },
            ];
        } else if (!isFreeEvent && !event.allowOffPlatformPayments) {
            withdrawnPlayerActions = [
                { label: 'Refund entry fees', value: 'refund' },
            ];
        }
    }

    let gridLayout = ['1fr', '1fr'];
    if (isPtsEvent) {
        gridLayout = ['1fr', '1fr', '1fr'];
    }
    if (isWTARankType) {
        gridLayout = ['1fr', '1fr', '1fr', '1fr'];
    }
    if (showPlayerStatus) {
        gridLayout = [...gridLayout, '1fr'];
        if (showPlayerPaymentStatus) {
            gridLayout = [...gridLayout, '1fr'];
        }
    }

    return (
        <div className="row">
            <div className="col">
                {event && event.eventDivisions && renderDivisonMenuItem()}
                {acceptance?.[playersTabState.divisionId]?.loading ? (
                    <p data-testid={dataTestId ? `${dataTestId}.loading-text` : undefined}>Loading players...</p>
                ) : (
                    <>
                        {acceptanceData?.loaded && playersTabState.selectedStatus.value !== 'withdrawn' && (
                            <>
                                {!showPlayers && isCurrentMemberPlaying && currentMember.registeredDivisions.some(d => d.divisionId === playersTabState.divisionId) && (
                                    <PlayerCard
                                        key={`player-${currentMember.id}`}
                                        player={currentMember}
                                        currentDivision={playersTabState.divisionId}
                                        showPlayerStatus={showPlayerStatus}
                                        showPlayerPaymentStatus={showPlayerPaymentStatus}
                                        showRatingNotLoggedIn={showRatingNotLoggedIn}
                                        showHighSchoolName={isOfficialHSEvent}
                                        utrType={event.utrType.value}
                                        addOptionsSpace={!!((playerActions && playerActions.length > 0))}
                                        showDecimals={isCurrentMemberPlaying}
                                        auth={auth}
                                        sendMessage={sendMessage}
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        hideRatings={false}
                                        ptsRank={isPtsEvent && (playerPtsRanks[currentMember.id] || '--')}
                                        hideUtrLabel={hideUtrLabel}
                                        sportTypeId={sportTypeId}
                                    />
                                )}
                                {!showPlayers && (
                                    <p data-testid="event-profile.players.hidden-message">Registered players are hidden.</p>
                                )}
                                {showPlayers && (
                                    <>
                                        {!!bucketPlayers && (
                                            buckets.map((bucket, i) => {
                                                const bucketPlayerActions = isRegistrationClosed ? buckets[i].playerActions : playerActions;
                                                return (
                                                    !!bucket.players.length && (
                                                        <Bucket
                                                            key={`bucket-${i}`}
                                                            buckets={buckets}
                                                            bucket={bucket}
                                                            playersTabState={playersTabState}
                                                            playerActions={bucketPlayerActions}
                                                            data-testid={dataTestId ? `${dataTestId}.bucket-${bucket.type || i}` : undefined}
                                                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                                                            currentDivision={currentDivision}
                                                            showPlayerStatus={showPlayerStatus}
                                                            showPlayerPaymentStatus={showPlayerPaymentStatus}
                                                            showRatingNotLoggedIn={showRatingNotLoggedIn}
                                                            showHighSchoolName={isOfficialHSEvent}
                                                            event={event}
                                                            auth={auth}
                                                            sendMessage={sendMessage}
                                                            menuOnSelected={props.playerMenuOnSelected}
                                                            isPtsEvent={isPtsEvent}
                                                            isWTARankType={isWTARankType}
                                                            playerPtsRanks={playerPtsRanks}
                                                            gridLayout={gridLayout}
                                                            handleListOrderSelect={(acceptanceType) => getAlternateListOrder(acceptanceType)}
                                                            hideUtrLabel={hideUtrLabel}
                                                        />
                                                    ));
                                            })
                                        )}
                                        {isRegistrationClosed && withdrawnTeamList.length > 0 && (
                                            <Bucket
                                                key={`bucket-withdrawn`}
                                                type={'Withdrawn'}
                                                teamList={withdrawnTeamList}
                                                playersTabState={playersTabState}
                                                data-testid={dataTestId ? `${dataTestId}.bucket-withdrawn` : undefined}
                                                playerActions={withdrawnPlayerActions}
                                                isCurrentMemberAdmin={isCurrentMemberAdmin}
                                                currentDivision={currentDivision}
                                                showPlayerStatus={showPlayerStatus}
                                                showPlayerPaymentStatus={showPlayerPaymentStatus}
                                                showRatingNotLoggedIn={showRatingNotLoggedIn}
                                                showHighSchoolName={isOfficialHSEvent}
                                                event={event}
                                                auth={auth}
                                                sendMessage={sendMessage}
                                                menuOnSelected={props.playerMenuOnSelected}
                                                isPtsEvent={isPtsEvent}
                                                isWTARankType={isWTARankType}
                                                playerPtsRanks={playerPtsRanks}
                                                gridLayout={gridLayout}
                                                hideUtrLabel={hideUtrLabel}
                                            />
                                        )}
                                        {!bucketPlayers && (
                                            <div>
                                                <p data-testid="event-profile.players.waiting-message">
                                                    Waiting for players<br />
                                                    Check back to view players registered for this event.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {(!acceptanceData || acceptanceData?.error || playersTabState.selectedStatus.value === 'withdrawn') && (
                            <>
                                {playersTabState && playersTabState.divisionId !== 'errors' && (
                                    <>
                                        <div className="d-flex text-small text-medium player-list-header">
                                            <div className="flex-grow">
                                                {(showPlayers && (teamList.length > 0)) &&
                                                    (
                                                        <div className="row">
                                                            <div className={`col-12 col-md-5`}>
                                                                <ColumnHeader
                                                                    isSortable
                                                                    columnName={(() => {
                                                                        return `${playerCount} ${pluralize('player', playerCount)}`;
                                                                    })()}
                                                                    isSelected={sortParam === 'name'}
                                                                    sortType={sortType}
                                                                    initialSort={SortType.ASC}
                                                                    onClick={(srtType) => {
                                                                        playersTabState.sortParam = 'name';
                                                                        playersTabState.sortType = srtType;
                                                                        dispatch(setPlayersTabState(playersTabState));
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className={`d-md-flex col-md-7`}>
                                                                <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                                                                    <div className={``}>
                                                                        <ColumnHeader
                                                                            isSortable
                                                                            columnName="Singles"
                                                                            isSelected={sortParam === 'singlesUtr'}
                                                                            sortType={sortType}
                                                                            onClick={(srtType) => {
                                                                                playersTabState.sortParam = 'singlesUtr';
                                                                                playersTabState.sortType = srtType;
                                                                                dispatch(setPlayersTabState(playersTabState));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className={``}>
                                                                        <ColumnHeader
                                                                            isSortable
                                                                            columnName="Doubles"
                                                                            isSelected={sortParam === 'doublesUtr'}
                                                                            sortType={sortType}
                                                                            onClick={(srtType) => {
                                                                                playersTabState.sortParam = 'doublesUtr';
                                                                                playersTabState.sortType = srtType;
                                                                                dispatch(setPlayersTabState(playersTabState));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    {isPtsEvent && (
                                                                        <div className={``}>
                                                                            <ColumnHeader
                                                                                columnName="PTT Rank"
                                                                                isSelected={sortParam === 'ptsRank'}
                                                                                sortType={sortType}
                                                                                onClick={(srtType) => {
                                                                                    playersTabState.sortParam = 'ptsRank';
                                                                                    playersTabState.sortType = srtType;
                                                                                    dispatch(setPlayersTabState(playersTabState));
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {showPlayerStatus && showPlayerPaymentStatus && isCurrentMemberAdmin && (
                                                                        <div className={`d-none d-md-block`}>
                                                                            <ColumnHeader columnName="Registration" />
                                                                        </div>
                                                                    )}
                                                                    {showPlayerStatus && showPlayerPaymentStatus && isCurrentMemberAdmin && (
                                                                        <div className={`d-none d-md-block`}>
                                                                            <ColumnHeader columnName="Payment" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                            <div className="bookmark">
                                                {playerActions && (
                                                    <div className="options" />
                                                )}
                                            </div>
                                        </div>

                                        {!showPlayers && isCurrentMemberPlaying && currentMember.registeredDivisions.some(d => d.divisionId === playersTabState.divisionId) && (
                                            <>
                                                <PlayerCard
                                                    key={`player-${currentMember.id}`}
                                                    player={currentMember}
                                                    currentDivision={playersTabState.divisionId}
                                                    showPlayerStatus={showPlayerStatus}
                                                    showPlayerPaymentStatus={showPlayerPaymentStatus}
                                                    showRatingNotLoggedIn={showRatingNotLoggedIn}
                                                    showHighSchoolName={isOfficialHSEvent}
                                                    utrType={event.utrType.value}
                                                    addOptionsSpace={!!((playerActions && playerActions.length > 0))}
                                                    showDecimals={isCurrentMemberPlaying}
                                                    auth={auth}
                                                    sendMessage={sendMessage}
                                                    isCurrentMemberAdmin={isCurrentMemberAdmin}
                                                    hideRatings={false}
                                                    ptsRank={isPtsEvent && (playerPtsRanks[currentMember.id] || '--')}
                                                    hideUtrLabel={hideUtrLabel}
                                                    sportTypeId={sportTypeId}
                                                />
                                            </>
                                        )}
                                        {showPlayers && (
                                            <>
                                                {teamList.length > 0 && teamList.map((team, i) => {
                                                    if (team.players.length > 0) {
                                                        return (
                                                            <div
                                                                className={`${styles.teamCard}`}
                                                                key={`team-${i}`}
                                                            >
                                                                {team.players.map((player, j) => {
                                                                    const playerDivision = _.find(player.registeredDivisions,
                                                                        { divisionId: playersTabState.divisionId });
                                                                    const actions = _.cloneDeep(playerActions);

                                                                    if (isCurrentMemberAdmin && currentDivision.price === 'Free' && playerDivision.paymentStatus === 'Paid' && playerDivision.registrationStatus !== 'Withdrawn') {
                                                                        const withdrawActionIndex = _.findIndex(playerActions, { value: 'withdraw' });
                                                                        actions[withdrawActionIndex].label = 'Withdraw and Refund';
                                                                    }

                                                                    if (isCurrentMemberAdmin && playersTabState.selectedStatus.value === 'withdrawn' && playerDivision.paymentStatus !== 'Paid') {
                                                                        _.remove(actions, { value: 'refund' });
                                                                    }

                                                                    return (
                                                                        <PlayerCard
                                                                            key={`player-${player.id}`}
                                                                            player={player}
                                                                            currentDivision={playersTabState.divisionId}
                                                                            showPlayerStatus={showPlayerStatus}
                                                                            showPlayerPaymentStatus={showPlayerPaymentStatus}
                                                                            showRatingNotLoggedIn={showRatingNotLoggedIn}
                                                                            showHighSchoolName={isOfficialHSEvent}
                                                                            utrType={event.utrType.value}
                                                                            menuOptions={j === 0 ? actions : null}
                                                                            menuOnSelected={(selected, e) => props.playerMenuOnSelected(e, player.id, selected)}
                                                                            addOptionsSpace={!!((playerActions && playerActions.length > 0))}
                                                                            showDecimals={isCurrentMemberAdmin}
                                                                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                                                                            auth={auth}
                                                                            sendMessage={sendMessage}
                                                                            hideRatings={false}
                                                                            ptsRank={isPtsEvent && (playerPtsRanks[player.id] || '--')}
                                                                            hideUtrLabel={hideUtrLabel}
                                                                            sportTypeId={sportTypeId}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                {teamList.length === 0 && (
                                                    <div>
                                                        {playersTabState.selectedStatus?.value === 'withdrawn' ? (
                                                            <p data-testid="event-profile.players.no-withdrawn-message">
                                                                There are no withdrawn players
                                                            </p>
                                                        ) : (
                                                            <p data-testid="event-profile.players.waiting-message">
                                                                Waiting for players<br />
                                                                Check back to view players registered for this event.
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {!showPlayers && (
                                            <p data-testid="event-profile.players.hidden-message">Registered players are hidden.</p>
                                        )}
                                    </>
                                )}
                                {event && event.membersWithoutProfile && event.membersWithoutProfile.length > 0 && playersTabState && playersTabState.divisionId === 'errors' && (
                                    <div>
                                        {event.membersWithoutProfile.map(m => (
                                            <MemberCard
                                                {...m}
                                                key={m.memberId}
                                                menuOptions={playerActions}
                                                menuOnSelected={(selected, e) => props.memberMenuOnSelected(e, m.memberId, selected)}
                                                sportTypeId={sportTypeId}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default withRouter(PlayersTabContent);
